import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  FaTicketAlt, FaTrophy, FaHandshake, FaChartLine,
  FaUsers, FaArrowRight, FaWallet, FaShieldAlt,
  FaCheck, FaPlay, FaCrown, FaDollarSign,
  FaGlobe, FaRobot, FaExchangeAlt, FaFootballBall,
  FaChevronLeft, FaChevronRight, FaRocket,
  FaUserPlus, FaPercentage,
} from 'react-icons/fa';
import HomeHeader from '../components/layout/HomeHeader';
import MobileBottomNav from '../components/layout/MobileBottomNav';
import MobileQuickPanel from '../components/layout/MobileQuickPanel';
import './Home.css';
import { Link, useNavigate } from 'react-router-dom';

const SLIDES = [
  {
    id: 1,
    emoji: '🏆',
    title: 'Fans to Fans Marketplace',
    description: 'Join thousands of passionate fans and predict on your favourite club games.',
    highlight: 'Predict & Win with Your Club',
    color: '#f7971e',
  },
  {
    id: 2,
    emoji: '⚡',
    title: 'Automated Payouts',
    description: 'Smart contracts handle payouts automatically when your prediction wins.',
    highlight: 'Instant Automated Payments',
    color: '#2ecc71',
  },
  {
    id: 3,
    emoji: '🤝',
    title: 'Compete with Fellow Fans',
    description: 'Challenge other fans of your club or rival clubs. Prove you know your team best.',
    highlight: 'Head-to-Head Fan Battles',
    color: '#3498db',
  },
  {
    id: 4,
    emoji: '🌍',
    title: 'Predict on Any Match',
    description: 'From Premier League to La Liga, predict on any match worldwide.',
    highlight: 'Global Match Coverage',
    color: '#9b59b6',
  },
  {
    id: 5,
    emoji: '🎉',
    title: 'Win Alongside Your Club',
    description: 'When your favourite club wins, you win too. Share the victory with fellow fans.',
    highlight: 'Win Together, Celebrate Together',
    color: '#e74c3c',
  },
];

const FEATURES = [
  { icon: <FaTicketAlt />, title: 'Share Bet Codes',           description: 'Post winning bet codes and help fellow fans win.',              color: '#f7971e' },
  { icon: <FaTrophy />,    title: 'Prediction Competitions',   description: 'Join competitions, predict match outcomes, and win cash.',      color: '#3498db' },
  { icon: <FaHandshake />, title: 'Fans vs Fans H2H',          description: 'Challenge rival fans head-to-head. Prove your club knowledge.', color: '#9b59b6' },
  { icon: <FaWallet />,    title: 'Automated Payouts',         description: 'Smart contract payouts. Win and get paid instantly.',           color: '#2ecc71' },
  { icon: <FaChartLine />, title: 'Rollover Challenges',       description: 'Sequential prediction challenges. Multiply your rewards.',      color: '#f39c12' },
  { icon: <FaShieldAlt />, title: 'Secure & Transparent',      description: 'Blockchain-powered platform. Transparent and secure.',          color: '#e74c3c' },
];

const CLUBS = [
  { name: 'Arsenal',      emoji: '🔴',    color: '#ef0107' },
  { name: 'Chelsea',      emoji: '🔵',    color: '#034694' },
  { name: 'Liverpool',    emoji: '🔴',    color: '#c8102e' },
  { name: 'Man City',     emoji: '🔵',    color: '#6cabe4' },
  { name: 'Man United',   emoji: '🔴',    color: '#da291c' },
  { name: 'Tottenham',    emoji: '⚪',    color: '#132257' },
  { name: 'Barcelona',    emoji: '🔴🔵',  color: '#a50044' },
  { name: 'Real Madrid',  emoji: '⚪',    color: '#00529f' },
  { name: 'Bayern Munich',emoji: '🔴',    color: '#dc052d' },
  { name: 'PSG',          emoji: '🔴🔵',  color: '#004170' },
];

const Home = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [quickPanelOpen, setQuickPanelOpen] = useState(false);

// Redirect logged-in users to the dashboard
useEffect(() => {
  if (isAuthenticated) {
    navigate('/dashboard', { replace: true });
  }
}, [isAuthenticated, navigate]);


  // Auto-advance the hero slider
  useEffect(() => {
    const t = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const nextSlide = () => setCurrentSlide((p) => (p + 1) % SLIDES.length);
  const prevSlide = () => setCurrentSlide((p) => (p - 1 + SLIDES.length) % SLIDES.length);

  const isPunterPlusActive = user?.punter_plus && user?.punter_plus_status !== 'expired';
  const isAdmin = user?.capabilities?.manage_options || false;

  return (
    <div className="home-page">
      {/* Marketing header — visible on all devices */}
      <HomeHeader />

      {/* ==================== HERO ==================== */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-pulse" />
              🚀 Ultimate Fans to Fans Sports Prediction Marketplace
            </div>

            <h1 className="hero-title">
              Predict, Compete & <br />
              <span className="gradient-text">Win With Your Club</span>
            </h1>

            <p className="hero-description">
              Join the ultimate fan-to-fan sports prediction marketplace.
              Predict on your favourite club games, compete with fellow fans,
              and get automated payouts when you win.
            </p>

            <div className="hero-features">
              <div className="hero-feature"><FaCheck className="hf-check" /> Predict on any match</div>
              <div className="hero-feature"><FaCheck className="hf-check" /> Automated payouts</div>
              <div className="hero-feature"><FaCheck className="hf-check" /> Win with your club</div>
            </div>

            <div className="hero-actions">
              {isAuthenticated ? (
                <Link to="/dashboard" className="btn btn-primary btn-hero">
                  Dashboard <FaArrowRight />
                </Link>
              ) : (
                <>
                  <Link to="/register" className="btn btn-primary btn-hero">
                    Join Free <FaRocket />
                  </Link>
                  <Link to="/login" className="btn btn-secondary btn-hero">
                    <FaPlay /> Login
                  </Link>
                </>
              )}
            </div>

            <div className="hero-trust">
              <span><FaShieldAlt className="trust-icon" /> Automated Payouts</span>
              <span><FaCheck className="trust-icon" /> 100% Transparent</span>
              <span><FaGlobe className="trust-icon" /> Global Platform</span>
            </div>
          </div>

          {/* Slider + Clubs */}
          <div className="hero-visual">
            <div className="slider-container">
              <div className="slider-wrapper">
                {SLIDES.map((slide, index) => (
                  <div
                    key={slide.id}
                    className={`slide-card ${index === currentSlide ? 'active' : ''}`}
                    style={{
                      transform: `translateX(${(index - currentSlide) * 100}%)`,
                      opacity: index === currentSlide ? 1 : 0.3,
                      zIndex: index === currentSlide ? 2 : 1,
                    }}
                  >
                    <div className="slide-content">
                      <div className="slide-emoji">{slide.emoji}</div>
                      <h3 className="slide-title">{slide.title}</h3>
                      <p className="slide-description">{slide.description}</p>
                      <div className="slide-highlight" style={{ background: slide.color }}>
                        <span>{slide.highlight}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="slider-controls">
                <button onClick={prevSlide} className="slider-btn" type="button" aria-label="Previous slide">
                  <FaChevronLeft />
                </button>
                <div className="slider-dots">
                  {SLIDES.map((_, i) => (
                    <button
                      key={i}
                      className={`dot ${i === currentSlide ? 'active' : ''}`}
                      onClick={() => setCurrentSlide(i)}
                      type="button"
                      aria-label={`Go to slide ${i + 1}`}
                    />
                  ))}
                </div>
                <button onClick={nextSlide} className="slider-btn" type="button" aria-label="Next slide">
                  <FaChevronRight />
                </button>
              </div>

              <div className="slide-counter">
                {currentSlide + 1} / {SLIDES.length}
              </div>
            </div>

            <div className="clubs-carousel">
              <div className="clubs-scroll">
                {[...CLUBS, ...CLUBS].map((club, i) => (
                  <div key={i} className="club-item">
                    <span className="club-emoji">{club.emoji}</span>
                    <span className="club-name" style={{ color: club.color }}>{club.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FANS ==================== */}
      <section className="fans-section">
        <div className="fans-container">
          <header className="fans-header">
            <span className="home-section-badge">🔥 Fans to Fans</span>
            <h2>Predict <span className="gradient-text">Alongside Your Club</span></h2>
            <p className="fans-subtitle">
              Join fellow fans, predict on your club's matches, and win together.
              When your club wins, you win too.
            </p>
          </header>

          <div className="fans-grid">
            <div className="fans-card">
              <div className="fans-icon">⚽</div>
              <h3>Predict on Club Games</h3>
              <p>Choose your favourite club and predict match outcomes.</p>
            </div>
            <div className="fans-card highlight">
              <div className="fans-icon">🤝</div>
              <h3>Compete with Fellow Fans</h3>
              <p>Challenge other fans and climb the leaderboard.</p>
            </div>
            <div className="fans-card">
              <div className="fans-icon">💰</div>
              <h3>Automated Payouts</h3>
              <p>Smart contracts handle payouts automatically.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FEATURES ==================== */}
      <section className="features-section" id="features">
        <header className="features-header">
          <span className="home-section-badge">Features</span>
          <h2>Everything You Need to <span className="gradient-text">Win</span></h2>
          <p className="features-subtitle">
            Built for fans, by fans. The ultimate sports prediction marketplace.
          </p>
        </header>

        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="feature-card">
              <div className="feature-icon-wrapper" style={{ background: `${f.color}20` }}>
                <span className="feature-icon" style={{ color: f.color }}>{f.icon}</span>
              </div>
              <h3>{f.title}</h3>
              <p>{f.description}</p>
              <div className="feature-line" style={{ background: f.color }} />
            </div>
          ))}
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="how-section" id="how">
        <div className="how-container">
          <header className="how-header">
            <span className="home-section-badge">How It Works</span>
            <h2>Start Winning in <span className="gradient-text">3 Simple Steps</span></h2>
          </header>

          <div className="steps-grid">
            {[
              { n: 1, icon: '📝', title: 'Join the Community',   text: 'Create your free account and choose your favourite clubs.' },
              { n: 2, icon: '🎯', title: 'Predict & Compete',    text: 'Predict on matches and compete with fellow fans.' },
              { n: 3, icon: '💰', title: 'Win & Get Paid',       text: 'Get automated payouts instantly when you win.' },
            ].map((s) => (
              <div key={s.n} className="step-card">
                <div className="step-number">{s.n}</div>
                <div className="step-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== PUNTER+ ==================== */}
      <section className="punter-section" id="punter">
        <div className="punter-container">
          <div className="punter-content">
            <div className="punter-text">
              <h2>Upgrade to <span className="gradient-text">Punter+</span></h2>
              <p>Unlock premium features and dominate the marketplace</p>

              <ul className="punter-benefits">
                <li><FaCheck className="check-icon" /> Create &amp; monetize private bet codes</li>
                <li><FaCheck className="check-icon" /> Create &amp; monetize competitions</li>
                <li><FaCheck className="check-icon" /> Create H2H games</li>
                <li><FaCheck className="check-icon" /> Create &amp; earn with Virtual H2H</li>
                <li><FaCheck className="check-icon" /> Create &amp; earn with Virtual Competitions</li>
                <li><FaCheck className="check-icon" /> Create subscription plans</li>
                <li><FaCheck className="check-icon" /> Create &amp; monetize rollovers</li>
              </ul>

              <Link
                to={isAuthenticated ? '/subscription' : '/register'}
                className="btn btn-primary"
              >
                {isAuthenticated ? 'Upgrade Now' : 'Join Now'} <FaArrowRight />
              </Link>
            </div>

            <div className="punter-price-card">
              <div className="price-card">
                <div className="price-header">
                  <FaCrown className="price-crown" />
                  <span className="price-plan">PUNTER+</span>
                </div>
                <div className="price-amount">
                  <span className="currency">$</span>
                  <span className="amount">2</span>
                  <span className="period">/mo</span>
                </div>
                <div className="price-features">
                  <div className="price-feature"><FaCheck /> All Premium Features</div>
                  <div className="price-feature"><FaCheck /> Unlimited Predictions</div>
                  <div className="price-feature"><FaCheck /> Community Monetization</div>
                </div>
                <Link
                  to={isAuthenticated ? '/subscription' : '/register'}
                  className="btn btn-primary btn-sm price-btn"
                >
                  {isAuthenticated ? 'Upgrade Now' : 'Get Started'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== TESTIMONIALS ==================== */}
      <section className="testimonials-section" id="testimonials">
        <div className="testimonials-container">
          <header className="testimonials-header">
            <span className="home-section-badge">Testimonials</span>
            <h2>What Fans Are <span className="gradient-text">Saying</span></h2>
          </header>

          <div className="testimonials-grid">
            {[
              { initials: 'JD', name: 'John Doe',  club: '🔴 Arsenal Fan',   text: 'XPunters is amazing! I predict on every game and win alongside my club.' },
              { initials: 'SM', name: 'Sarah M.',  club: '🔵 Chelsea Fan',   text: 'The automated payouts are incredible. Win and get paid instantly.' },
              { initials: 'MK', name: 'Mike K.',   club: '🔴 Liverpool Fan', text: 'Competing with fellow fans makes every match more exciting!' },
            ].map((t) => (
              <div key={t.initials} className="testimonial-card">
                <div className="testimonial-avatar">{t.initials}</div>
                <h4>{t.name}</h4>
                <div className="testimonial-club">{t.club}</div>
                <p>"{t.text}"</p>
                <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== REFERRAL ==================== */}
      <section className="referral-section" id="referral">
        <div className="referral-container">
          <header className="referral-header">
            <span className="home-section-badge">🎁 Refer &amp; Earn</span>
            <h2>Invite Friends, <span className="gradient-text">Earn Passive Income</span></h2>
            <p className="referral-subtitle">
              Share your referral link and earn 20% of admin fees whenever your friends win.
            </p>
          </header>

          <div className="referral-content">
            <div className="referral-card">
              <div className="referral-icon-wrapper">
                <FaUserPlus className="referral-icon" />
              </div>
              <h3>1. Share Your Link</h3>
              <p>Get your unique referral link and share it with friends</p>
              <div className="referral-benefits">
                <div className="referral-benefit"><FaCheck className="rb-check" /> 20% of admin fees from referrals</div>
                <div className="referral-benefit"><FaCheck className="rb-check" /> Earn every time your referral wins</div>
                <div className="referral-benefit"><FaCheck className="rb-check" /> Passive income stream</div>
              </div>
            </div>

            <div className="referral-card highlight">
              <div className="referral-icon-wrapper gold">
                <FaPercentage className="referral-icon" />
              </div>
              <h3>2. Friend Joins &amp; Wins</h3>
              <p>Your friend joins using your link and starts winning predictions</p>
              <div className="referral-steps">
                <div className="step-badge">1</div><span>Friend registers with your link</span>
                <div className="step-badge">2</div><span>They make predictions &amp; win</span>
                <div className="step-badge">3</div><span>You earn 20% of admin fees</span>
              </div>
            </div>

            <div className="referral-card">
              <div className="referral-icon-wrapper green">
                <FaDollarSign className="referral-icon" />
              </div>
              <h3>3. Get Paid Automatically</h3>
              <p>No manual claims. Earnings are automatically credited to your wallet</p>
              <div className="referral-rewards">
                <div className="reward-item">
                  <span className="reward-amount">20%</span>
                  <span className="reward-label">Of admin fees</span>
                </div>
                <div className="reward-divider" />
                <div className="reward-item">
                  <span className="reward-amount">Auto</span>
                  <span className="reward-label">Instant payouts</span>
                </div>
                <div className="reward-divider" />
                <div className="reward-item">
                  <span className="reward-amount">Unlimited</span>
                  <span className="reward-label">Referral earnings</span>
                </div>
              </div>
            </div>
          </div>

          <div className="referral-cta">
            {isAuthenticated ? (
              <Link to="/referral" className="btn btn-primary btn-lg">
                View Referral Dashboard <FaArrowRight />
              </Link>
            ) : (
              <Link to="/register" className="btn btn-primary btn-lg">
                Start Earning <FaRocket />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="cta-section">
        <div className="cta-container">
          <div className="cta-content">
            <div className="cta-icon">🚀</div>
            <h2>Ready to Win With Your Club?</h2>
            <p>Join the ultimate fans to fans sports prediction marketplace today.</p>

            {!isAuthenticated ? (
              <div className="cta-actions">
                <Link to="/register" className="btn btn-primary btn-lg">
                  Create Free Account <FaArrowRight />
                </Link>
                <Link to="/login" className="btn btn-secondary btn-lg">
                  <FaPlay /> Login
                </Link>
              </div>
            ) : (
              <div className="cta-actions">
                <Link to="/dashboard" className="btn btn-primary btn-lg">
                  Go to Dashboard <FaArrowRight />
                </Link>
              </div>
            )}

            <div className="cta-trust">
              <span>🔒 Automated Payouts</span>
              <span>💳 100% Transparent</span>
              <span>🌍 Global Community</span>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== MOBILE BOTTOM NAV (only for logged-in users) ==================== */}
      {isAuthenticated && (
        <>
          <MobileBottomNav
            quickOpen={quickPanelOpen}
            onQuickToggle={() => setQuickPanelOpen((o) => !o)}
          />
          <MobileQuickPanel
            open={quickPanelOpen}
            onClose={() => setQuickPanelOpen(false)}
            user={user}
            isAdmin={isAdmin}
            isPunterPlusActive={isPunterPlusActive}
          />
        </>
      )}
    </div>
  );
};

export default Home;